import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, User, Tag, ArrowLeft, Eye, ChevronRight, Search } from "lucide-react";

/* ── tiny markdown renderer (no external deps) ───────────────────────── */
function renderMarkdown(text = "") {
  const esc = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const lines = text.split("\n");
  const out = [];
  let inUl = false, inOl = false, ulItems = [], olItems = [];

  const flushUl = () => {
    if (inUl) { out.push(`<ul class="md-ul">${ulItems.join("")}</ul>`); inUl = false; ulItems = []; }
  };
  const flushOl = () => {
    if (inOl) { out.push(`<ol class="md-ol">${olItems.join("")}</ol>`); inOl = false; olItems = []; }
  };

  const inline = (s) => {
    return s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, '<code class="md-code">$1</code>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="md-link" target="_blank" rel="noreferrer">$1</a>');
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#{1}\s/.test(line)) { flushUl(); flushOl(); out.push(`<h1 class="md-h1">${inline(esc(line.slice(2)))}</h1>`); }
    else if (/^#{2}\s/.test(line)) { flushUl(); flushOl(); out.push(`<h2 class="md-h2">${inline(esc(line.slice(3)))}</h2>`); }
    else if (/^#{3}\s/.test(line)) { flushUl(); flushOl(); out.push(`<h3 class="md-h3">${inline(esc(line.slice(4)))}</h3>`); }
    else if (/^- /.test(line)) { flushOl(); inUl = true; ulItems.push(`<li>${inline(esc(line.slice(2)))}</li>`); }
    else if (/^\d+\. /.test(line)) { flushUl(); inOl = true; olItems.push(`<li>${inline(esc(line.replace(/^\d+\.\s/, "")))}</li>`); }
    else if (line.startsWith("> ")) { flushUl(); flushOl(); out.push(`<blockquote class="md-blockquote">${inline(esc(line.slice(2)))}</blockquote>`); }
    else if (line.trim() === "") { flushUl(); flushOl(); out.push("<br/>"); }
    else { flushUl(); flushOl(); out.push(`<p class="md-p">${inline(esc(line))}</p>`); }
  }
  flushUl(); flushOl();
  return out.join("");
}

/* ── shared styles ────────────────────────────────────────────────────── */
const STYLES = `
  

  .blog-root { font-family: 'DM Sans', sans-serif; background: #f8fafc; min-height: 100vh; }

  /* LISTING HERO */
  .blog-hero {
    background: #000000;
    padding: 56px 24px 72px;
    text-align: center;
    position: relative; overflow: hidden;
  }
  .blog-hero::before {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px);
    background-size: 26px 26px;
  }
  .blog-hero::after {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 55% at 50% 0%, rgba(29,111,242,0.22) 0%, transparent 70%);
  }
  .blog-hero-inner { position: relative; z-index: 1; }
  .blog-eyebrow {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(29,111,242,0.15); border: 1px solid rgba(29,111,242,0.35);
    color: #93c5fd; border-radius: 99px; padding: 5px 16px;
    font-size: 12px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase;
    margin-bottom: 20px;
  }
  .blog-hero h1 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(34px, 5.5vw, 60px);
    font-weight: 800; color: #fff;
    letter-spacing: -0.03em; line-height: 1.08; margin: 0 0 16px;
  }
  .blog-hero h1 span { color: #60a5fa; }
  .blog-hero p { color: #94a3b8; font-size: 16px; max-width: 480px; margin: 0 auto 36px; font-weight: 300; line-height: 1.65; }

  /* SEARCH BAR */
  .blog-search {
    display: flex; align-items: center;
    max-width: 420px; margin: 0 auto;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px; padding: 0 16px;
    gap: 10px;
    transition: border-color 0.2s, background 0.2s;
  }
  .blog-search:focus-within { border-color: rgba(29,111,242,0.6); background: rgba(255,255,255,0.1); }
  .blog-search input {
    background: transparent; border: none; outline: none;
    color: #f1f5f9; font-family: 'DM Sans', sans-serif;
    font-size: 15px; padding: 12px 0; flex: 1;
  }
  .blog-search input::placeholder { color: #64748b; }
  .blog-search svg { color: #64748b; flex-shrink: 0; }

  /* TAG FILTERS */
  .tag-bar {
    background: #fff; border-bottom: 1px solid #e2e8f0;
    padding: 14px 24px; display: flex; align-items: center;
    gap: 10px; overflow-x: auto; white-space: nowrap;
    scrollbar-width: none;
  }
  .tag-bar::-webkit-scrollbar { display: none; }
  .tag-btn {
    display: inline-flex; align-items: center; gap: 5px;
    border: 1px solid #e2e8f0; border-radius: 99px;
    padding: 5px 14px; font-size: 13px; font-weight: 500;
    cursor: pointer; background: #fff; color: #475569;
    transition: all 0.15s; white-space: nowrap;
  }
  .tag-btn:hover { border-color: #93c5fd; color: #1D6FF2; background: #eff6ff; }
  .tag-btn.active { background: #1D6FF2; color: #fff; border-color: #1D6FF2; }

  /* GRID */
  .blog-grid-wrap { max-width: 1100px; margin: 0 auto; padding: 48px 24px 80px; }
  .blog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }

  /* CARD */
  .blog-card {
    background: #fff; border: 1px solid #e2e8f0; border-radius: 20px;
    overflow: hidden; display: flex; flex-direction: column;
    transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
    text-decoration: none; color: inherit;
  }
  .blog-card:hover { box-shadow: 0 12px 36px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #bfdbfe; }
  .blog-card-img { width: 100%; height: 200px; object-fit: cover; display: block; background: #e2e8f0; }
  .blog-card-img-placeholder {
    width: 100%; height: 200px;
    background: linear-gradient(135deg, #1e293b 0%, #1e3a5f 100%);
    display: flex; align-items: center; justify-content: center;
  }
  .blog-card-body { padding: 22px; flex: 1; display: flex; flex-direction: column; }
  .blog-card-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
  .blog-tag {
    background: #eff6ff; color: #1D6FF2;
    border-radius: 99px; padding: 3px 10px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  }
  .blog-card h2 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 19px; font-weight: 700; color: #0f172a;
    line-height: 1.3; margin: 0 0 10px;
    letter-spacing: -0.01em;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .blog-card p {
    color: #64748b; font-size: 14px; line-height: 1.65;
    flex: 1; margin: 0 0 16px;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .blog-meta { display: flex; align-items: center; gap: 14px; font-size: 12.5px; color: #94a3b8; }
  .blog-meta-item { display: flex; align-items: center; gap: 5px; }
  .blog-read-more { display: flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; color: #1D6FF2; margin-top: 14px; }

  /* FEATURED (first card larger) */
  .blog-card.featured { grid-column: 1 / -1; flex-direction: row; }
  @media (max-width: 640px) { .blog-card.featured { flex-direction: column; } }
  .blog-card.featured .blog-card-img { width: 42%; height: auto; min-height: 280px; flex-shrink: 0; }
  .blog-card.featured .blog-card-img-placeholder { width: 42%; min-height: 280px; flex-shrink: 0; }
  .blog-card.featured h2 { font-size: 26px; -webkit-line-clamp: 3; }
  .blog-card.featured p { font-size: 15px; -webkit-line-clamp: 4; }
  @media (max-width: 640px) {
    .blog-card.featured .blog-card-img, .blog-card.featured .blog-card-img-placeholder { width: 100%; min-height: 200px; }
  }

  /* ── DETAIL PAGE ── */
  .detail-root { max-width: 760px; margin: 0 auto; padding: 48px 24px 80px; }
  .detail-back {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 14px; font-weight: 500; color: #64748b;
    text-decoration: none; margin-bottom: 32px;
    transition: color 0.15s;
  }
  .detail-back:hover { color: #1D6FF2; }
  .detail-cover { width: 100%; border-radius: 16px; object-fit: cover; max-height: 420px; margin-bottom: 36px; }
  .detail-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
  .detail-tag {
    background: #eff6ff; color: #1D6FF2;
    border-radius: 99px; padding: 4px 12px;
    font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  }
  .detail-title {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(26px, 4vw, 42px);
    font-weight: 800; color: #0f172a;
    line-height: 1.1; letter-spacing: -0.025em;
    margin: 0 0 20px;
  }
  .detail-meta {
    display: flex; flex-wrap: wrap; align-items: center; gap: 18px;
    padding: 16px 0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;
    margin-bottom: 36px; font-size: 14px; color: #64748b;
  }
  .detail-meta-item { display: flex; align-items: center; gap: 6px; }
  .author-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, #1D6FF2, #7c3aed);
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0;
  }

  /* MARKDOWN BODY */
  .blog-body { font-family: 'Lora', serif; font-size: 17px; line-height: 1.82; color: #1e293b; }
  .blog-body .md-h1 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 28px; font-weight: 800; margin: 36px 0 14px; color: #0f172a; letter-spacing: -0.02em; }
  .blog-body .md-h2 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 22px; font-weight: 700; margin: 32px 0 12px; color: #0f172a; letter-spacing: -0.015em; }
  .blog-body .md-h3 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 18px; font-weight: 700; margin: 24px 0 8px; color: #1e293b; }
  .blog-body .md-p { margin: 0 0 18px; }
  .blog-body .md-ul, .blog-body .md-ol { padding-left: 24px; margin: 0 0 18px; }
  .blog-body .md-ul li, .blog-body .md-ol li { margin-bottom: 6px; }
  .blog-body .md-blockquote {
    border-left: 4px solid #1D6FF2; padding: 14px 20px;
    background: #eff6ff; border-radius: 0 10px 10px 0;
    margin: 20px 0; font-style: italic; color: #334155;
  }
  .blog-body .md-code { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 4px; padding: 1px 6px; font-family: monospace; font-size: 14px; font-style: normal; }
  .blog-body .md-link { color: #1D6FF2; text-decoration: underline; }

  /* EMPTY / LOADING */
  .blog-empty { text-align: center; padding: 60px 24px; color: #94a3b8; }
  .blog-spinner { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top-color: #1D6FF2; border-radius: 50%; animation: spin 0.7s linear infinite; margin: 60px auto; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

/* ── date formatter ─────────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

/* ── BLOG LISTING ───────────────────────────────────────────────────── */
export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [allTags, setAllTags] = useState([]);

  const load = async (tag, query) => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (tag) params.tag = tag;
      if (query) params.q = query;
      const r = await api.get("/blogs", { params });
      const items = r.data.items || [];
      setPosts(items);
      // collect all tags
      const tags = [...new Set(items.flatMap(p => p.tags || []))];
      setAllTags(tags);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load("", ""); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(activeTag, q);
  };

  const handleTag = (t) => {
    const next = activeTag === t ? "" : t;
    setActiveTag(next);
    load(next, q);
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="blog-root">
        {/* Hero */}
        <div className="blog-hero">
          <div className="blog-hero-inner">
            <h1>Insights for every job seeker</h1>
            <p>Career tips, hiring guides, and local market insights — written by people who know the Indian job market inside out.</p>
            <form onSubmit={handleSearch} className="blog-search">
              <Search size={16} />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search articles…"
              />
            </form>
          </div>
        </div>

        {/* Tag filter bar */}
        {allTags.length > 0 && (
          <div className="tag-bar">
            <button className={`tag-btn ${!activeTag ? "active" : ""}`} onClick={() => handleTag("")}>All</button>
            {allTags.map(t => (
              <button key={t} className={`tag-btn ${activeTag === t ? "active" : ""}`} onClick={() => handleTag(t)}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="blog-grid-wrap">
          {loading ? (
            <div className="blog-spinner" />
          ) : posts.length === 0 ? (
            <div className="blog-empty">
              <p className="text-lg font-semibold text-slate-600 mb-2">No posts found</p>
              <p className="text-sm">Try a different search term or tag.</p>
            </div>
          ) : (
            <div className="blog-grid">
              {posts.map((post, i) => (
                <BlogCard key={post.id} post={post} featured={i === 0} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function BlogCard({ post, featured }) {
  return (
    <Link to={`/blog/${post.slug}`} className={`blog-card ${featured ? "featured" : ""}`}>
      {post.cover_image ? (
        <img src={post.cover_image} alt={post.title} className="blog-card-img" />
      ) : (
        <div className="blog-card-img-placeholder">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        </div>
      )}
      <div className="blog-card-body">
        {post.tags?.length > 0 && (
          <div className="blog-card-tags">
            {post.tags.slice(0, 3).map(t => <span key={t} className="blog-tag">{t}</span>)}
          </div>
        )}
        <h2>{post.title}</h2>
        <p>{post.excerpt || "Read more about this topic…"}</p>
        <div className="blog-meta">
          <span className="blog-meta-item"><User size={12} /> {post.author_name || "Admin"}</span>
          <span className="blog-meta-item"><Calendar size={12} /> {fmtDate(post.created_at)}</span>
          {post.views > 0 && <span className="blog-meta-item"><Eye size={12} /> {post.views}</span>}
        </div>
        <div className="blog-read-more">Read article <ChevronRight size={14} /></div>
      </div>
    </Link>
  );
}

/* ── BLOG DETAIL ────────────────────────────────────────────────────── */
export function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/blogs/${slug}`)
      .then(r => setPost(r.data))
      .catch(e => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <>
      <style>{STYLES}</style>
      <div className="blog-root">
        <div className="detail-root">
          <Link to="/blog" className="detail-back"><ArrowLeft size={15} /> Back to Blog</Link>

          {loading && <div className="blog-spinner" />}

          {notFound && (
            <div className="blog-empty">
              <p className="text-lg font-semibold text-slate-600 mb-2">Article not found</p>
              <p className="text-sm">It may have been removed or the link is incorrect.</p>
              <button onClick={() => navigate("/blog")} className="mt-6 text-[#1D6FF2] font-medium text-sm">← Go back</button>
            </div>
          )}

          {post && (
            <>
              {/* Tags */}
              {post.tags?.length > 0 && (
                <div className="detail-tags">
                  {post.tags.map(t => <span key={t} className="detail-tag">{t}</span>)}
                </div>
              )}

              {/* Title */}
              <h1 className="detail-title">{post.title}</h1>

              {/* Meta */}
              <div className="detail-meta">
                <span className="detail-meta-item" style={{ gap: 10 }}>
                  <span className="author-avatar">{(post.author_name || "A")[0].toUpperCase()}</span>
                  <span style={{ fontWeight: 500, color: "#0f172a" }}>{post.author_name || "Admin"}</span>
                </span>
                <span className="detail-meta-item"><Calendar size={14} />{fmtDate(post.created_at)}</span>
                {post.views > 0 && <span className="detail-meta-item"><Eye size={14} />{post.views} views</span>}
              </div>

              {/* Cover */}
              {post.cover_image && (
                <img src={post.cover_image} alt={post.title} className="detail-cover" />
              )}

              {/* Body */}
              <div
                className="blog-body"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* Default export for convenience (listing) */
export default BlogList;